import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SupportedLocaleCode } from '@ttc/ttc-constants/locale';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { AccountBackend } from '@libs/src/account/account.backend';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupMatchConfigStoreService } from '@libs/src/group-match-config/group-match-config-store.service';
import { GroupsBackend } from '@libs/src/groups/groups.backend';
import { LocaleBackend } from '@libs/src/locale/locale.backend';
import { LoggerService } from '@libs/src/logger/logger.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { LocaleSettingsService } from '@main-client/src/app/core/locale-settings.service';
import { TranslationLocalStorageService } from '@main-client/src/app/translation-local-storage/translation-local-storage.service';
import { UserOnboardingHubPreferencesDialogService } from '@main-client/src/app/user-onboarding/user-onboarding-hub-preferences-dialog/user-onboarding-hub-preferences-dialog.service';
import { UserOnboardingService } from '@main-client/src/app/user-onboarding/user-onboarding.service';

import { fadeInEnter } from '@libs/src/animations/animations';
import { UserContentTranslation } from '@libs/src/locale-lookup/locale-translations-map.interface';
import { getLocaleDisplayName } from '@libs/src/locale/locale.helper';
import { GroupLocalesResponse } from '@libs/src/locale/locale.interface';
import {
  Account,
  AccountGroupMembership,
} from '@libs/src/models/account.model';
import { Group } from '@libs/src/models/group.model';
import {
  ProgramMembership,
  ProgramMembershipStatus,
  programMembershipStatus,
} from '@libs/src/models/program-membership.model';
import { programTypes } from '@libs/src/models/program.model';
import { findMatchMakingPhase } from '@libs/src/program-memberships/program-membership.helper';
import { Tenant } from '@libs/src/tenant/tenant.interface';
import { SetUser } from '@libs/src/user/user.actions';
import { getMembership } from '@main-client/src/app/account/account.helpers';
import { IAppState } from '@main-client/src/app/app.state';
import { GetHubs } from '@main-client/src/app/hubs/hubs.actions';
import {
  USER_ONBOARDING_ROUTE_PATHS,
  USER_ONBOARDING_ROUTES_BASE_PATH,
} from '@main-client/src/app/user-onboarding/user-onboarding.helper';
import {
  hasSignedUpViaDevelopmentProgram,
  hasSignedUpViaPathway,
} from '@main-client/src/app/user/user.helpers';

import { LocaleManagerModalService } from '../locale-manager/locale-manager-modal.service';

import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import values from 'lodash-es/values';

const abandonOnboardingPromptText =
  "You haven't finished setting up your account. Are you sure you want to leave?";
const statusInactive = 'inactive';

@Component({
  animations: [fadeInEnter],
  providers: [DestroySubscriptionsService],
  selector: 'app-user-onboarding',
  templateUrl: './user-onboarding.component.html',
})
export class UserOnboardingComponent implements OnInit, OnDestroy {
  viewMode: string;
  USER_ONBOARDING_ROUTE_PATHS = USER_ONBOARDING_ROUTE_PATHS;
  userState = this.store.select('user');
  group: Observable<Group>;
  hasPreregistrationGroup: boolean;
  isLoading = false;
  localeDisplayName: string;
  tenant$ = this.store.select('tenant');
  abandonOnboardingPrompt = (event: any) =>
    this.showAbandonOnboardingPrompt(event);
  numberOfSteps = 5;
  memberships: AccountGroupMembership[];
  preregistrationMemberships: AccountGroupMembership[];
  programMemberships: ProgramMembership[];

  constructor(
    @Inject(LOCALE_ID) public appLocale: SupportedLocaleCode,
    public accountBackend: AccountBackend,
    public destroy$: DestroySubscriptionsService,
    public groupMatchConfigStoreService: GroupMatchConfigStoreService,
    public groupsBackend: GroupsBackend,
    private readonly localeBackend: LocaleBackend,
    private readonly localeManagerModalService: LocaleManagerModalService,
    private readonly localeSettingsService: LocaleSettingsService,
    public logger: LoggerService,
    public route: ActivatedRoute,
    public router: Router,
    public store: Store<IAppState>,
    public toastService: ToastService,
    private readonly userOnboardingService: UserOnboardingService,
    public userOnboardingHubPreferencesDialogService: UserOnboardingHubPreferencesDialogService,
    private readonly translationStorageService: TranslationLocalStorageService,
  ) {}

  ngOnInit() {
    document.body.classList.add('bg-color-white');
    window.addEventListener('beforeunload', this.abandonOnboardingPrompt);
    this.viewMode = this.route.snapshot.data.viewMode;
    this.localeDisplayName = getLocaleDisplayName(this.appLocale);
    if (USER_ONBOARDING_ROUTE_PATHS.hubs === this.viewMode) {
      this.store.dispatch(GetHubs({}));
      return;
    }

    const { preregistrationGroupId, selectedGroupId } =
      this.route.snapshot.queryParams;
    this.hasPreregistrationGroup = !!preregistrationGroupId;
    this.group = this.getGroup(preregistrationGroupId || selectedGroupId);
    this.group
      .pipe(
        concatMap((group: Group) => {
          const groupLocales$ = this.localeBackend.getGroupLocales(group).pipe(
            catchError(() => EMPTY),
            tap((result: GroupLocalesResponse) => {
              this.translationStorageService.setTranslationsMap(
                result.groupLocales as UserContentTranslation[],
              );
            }),
          );
          const programMemberships$ = this.accountBackend.v3
            .getAccountGroupsProgramMemberships(group)
            .pipe(
              map(({ programMemberships }) =>
                programMemberships.filter(({ program, status }) => {
                  const matchMakingPhase = findMatchMakingPhase(program);
                  const validStatuses: readonly ProgramMembershipStatus[] = [
                    programMembershipStatus.active,
                    programMembershipStatus.declined,
                    programMembershipStatus.invited,
                    programMembershipStatus.ready_to_invite,
                    programMembershipStatus.pending,
                  ];
                  const isAlwaysOnProgram =
                    programTypes.alwaysOn === program.program_type;
                  const isRecuringProgram =
                    programTypes.repeat === program.program_type;
                  return (
                    validStatuses.includes(status) &&
                    (!matchMakingPhase.phase_completed ||
                      isAlwaysOnProgram ||
                      isRecuringProgram)
                  );
                }),
              ),
              tap((programMemberships) => {
                this.programMemberships = programMemberships;
              }),
            );
          const userLocaleConfig$ = this.tenant$.pipe(
            concatMap((tenant: Tenant) =>
              this.accountBackend.v2.getAccountLocaleConfig().pipe(
                map((result) => result.localeConfig),
                tap((result) => {
                  if (result?.locale_selected !== this.appLocale) {
                    this.localeSettingsService.ensureBrowserLocale(
                      tenant,
                      result?.locale_selected,
                    );
                  }
                }),
              ),
            ),
          );
          return combineLatest([
            groupLocales$,
            programMemberships$,
            userLocaleConfig$,
          ]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    combineLatest([
      this.userState,
      this.hasPreregistrationGroup
        ? this.accountBackend.v3
            .getAccountPreregistration()
            .pipe(catchError(() => of({ preregistration: null })))
        : of({ preregistration: null }),
    ])
      .pipe(
        takeUntil(this.destroy$),
        concatMap(([user, response]) => {
          this.preregistrationMemberships =
            response?.preregistration?.groups ?? [];
          const preRegMembership = getMembership(
            this.preregistrationMemberships,
            preregistrationGroupId,
          );
          const userMembership = getMembership(
            user.groups,
            preregistrationGroupId,
          );
          if (
            !!preregistrationGroupId &&
            isEmpty(userMembership) &&
            !isEmpty(preRegMembership)
          ) {
            return this.accountBackend
              .putAccountGroups(
                { _id: preregistrationGroupId },
                preRegMembership,
              )
              .pipe(
                map((updatedMemberships) => ({
                  ...user,
                  groups: updatedMemberships,
                })),
                catchError(() => of(user)),
              );
          }
          return of(user);
        }),
        tap((user) => {
          this.memberships = [...user.groups, ...(user.pending_groups || [])];
        }),
      )
      .subscribe();
  }

  ngOnDestroy() {
    window.removeEventListener('beforeunload', this.abandonOnboardingPrompt);
    document.body.classList.remove('bg-color-white');
  }

  private showAbandonOnboardingPrompt(event: any) {
    event.preventDefault();
    event.returnValue = abandonOnboardingPromptText;
    this.logger
      .logInfo({
        message: 'Onboarding - Interaction',
        properties: {
          actionType: 'onboarding-dismiss-session-dialog-shown',
          elementCustomId: this.viewMode,
          windowUrl: window.location.href,
        },
      })
      .subscribe();
    return abandonOnboardingPromptText;
  }

  getGroup(groupId: string) {
    return this.groupsBackend.getGroup({ _id: groupId }).pipe(
      map((group: Group) => {
        const isInvalidGroup = isEqual(statusInactive, group.status);
        if (isInvalidGroup) {
          this.navigateToOnboardingView('hubs', null, true);
          return null;
        }
        return group;
      }),
      catchError(() => {
        this.navigateToOnboardingView('hubs', null, true);
        return EMPTY;
      }),
    );
  }

  getMembershipByGroupId(groupId: string) {
    return this.userState.pipe(
      take(1),
      map((account: Account) => {
        const membership = getMembership(account.groups, groupId);
        return membership ? membership.document : null;
      }),
    );
  }

  navigateToOnboardingView(
    requestedView: string,
    group?: Group,
    replaceUrl?: boolean,
  ) {
    let nextView = requestedView;
    if (
      USER_ONBOARDING_ROUTE_PATHS.hubInterests === requestedView &&
      !group.industries?.length
    ) {
      nextView = USER_ONBOARDING_ROUTE_PATHS.hubGoals;
    }
    const queryParamKey = this.hasPreregistrationGroup
      ? 'preregistrationGroupId'
      : 'selectedGroupId';
    const validViews = values(USER_ONBOARDING_ROUTE_PATHS);
    const defaultView = this.USER_ONBOARDING_ROUTE_PATHS.hubs;
    const viewToNavigateTo =
      validViews.find((validView) => validView === nextView) || defaultView;
    const queryParams =
      (group && group._id && { [queryParamKey]: group._id }) || null;
    this.router.navigate([USER_ONBOARDING_ROUTES_BASE_PATH, viewToNavigateTo], {
      queryParams,
      replaceUrl,
      queryParamsHandling: 'merge',
    });
  }

  navigateBasedOnGroup(group: Group) {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    return this.getMembershipByGroupId(group._id).subscribe(
      (membership: AccountGroupMembership) => {
        if (!isEmpty(membership)) {
          return this.navigateToOnboardingView(
            USER_ONBOARDING_ROUTE_PATHS.hubSegments,
            group,
          );
        }
        return this.accountBackend
          .putAccountGroups(group)
          .pipe(
            tap((response: AccountGroupMembership[]) => {
              this.store.dispatch(SetUser({ groups: response }));
            }),
            tap(() => {
              this.navigateToOnboardingView(
                USER_ONBOARDING_ROUTE_PATHS.hubSegments,
                group,
              );
            }),
            catchError((response: HttpErrorResponse) => {
              this.isLoading = false;
              if (403 === response.status) {
                this.navigateToOnboardingView(
                  USER_ONBOARDING_ROUTE_PATHS.hubSegments,
                  group,
                  true,
                );
                return EMPTY;
              }
              this.toastService.showError(response.error.message);
              return EMPTY;
            }),
          )
          .subscribe();
      },
    );
  }

  navigateOnOnboardingFinish(group: Group, tenant: Tenant, user: Account) {
    return this.userOnboardingService
      .onOnboardingSuccess(group, tenant)
      .subscribe(() => {
        const pathwayIdQueryParam =
          this.route.snapshot.queryParamMap.get('pathwayId');
        if (hasSignedUpViaPathway(user) || pathwayIdQueryParam) {
          const pathwayId = hasSignedUpViaPathway(user)
            ? user.signup_method_details
            : pathwayIdQueryParam;
          return this.router.navigate([
            '/hub',
            group.slug,
            'pathways',
            pathwayId,
          ]);
        }
        const programIdQueryParam =
          this.route.snapshot.queryParamMap.get('programId');
        if (hasSignedUpViaDevelopmentProgram(user) || programIdQueryParam) {
          const programId = hasSignedUpViaDevelopmentProgram(user)
            ? user.signup_method_details
            : programIdQueryParam;
          return this.router.navigate([
            '/hub',
            group.slug,
            'programs',
            programId,
          ]);
        }
        if (group.community_page_information?.is_visible) {
          return this.router.navigate(['/hub', group.slug, 'community']);
        }
        this.router.navigate(['/profile']);
      });
  }

  openPreferencesDialog(group: Group, tenant: Tenant) {
    return this.userOnboardingHubPreferencesDialogService
      .openDialog({ group })
      .afterClosed()
      .pipe(
        filter(
          (memberships: AccountGroupMembership[]) => !isEmpty(memberships),
        ),
        tap(() =>
          this.userOnboardingService
            .onOnboardingSuccess(group, tenant)
            .subscribe(() => this.router.navigate(['/profile'])),
        ),
      );
  }

  hasValidAccountGroupMembership(group: Group, user: Account) {
    return !isEmpty(getMembership(user.groups, group._id));
  }

  openLanguageManager() {
    this.userState
      .pipe(
        take(1),
        tap((user) => {
          this.localeManagerModalService.openLanguageManager(user);
        }),
        catchError(() => {
          this.toastService.showError('failed to get account');
          return EMPTY;
        }),
      )
      .subscribe();
  }
}
